import { Delete as DeleteIcon } from "@mui/icons-material";
import { Box, Stack } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { readFirstSheet } from "common/excel";
import Component from "components/Component";
import Button from "components/common/Button";
import { makePrettyNumberColDef } from "components/common/dataGrid/prettyNumberColumn";
import * as labels from "config.labels";
import { useSnackbar } from "notistack";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import * as expiringProgramActions from "store/actions/input/expiringProgram/expiringProgramActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as expiringProgramSelectors from "store/selectors/input/expiringProgram/expiringProgramSelectors";

const EDITABLE_FIELDS = ["carrier", "limit", "attachment", "grossPremium"];
const IMPORT_ACCEPT = ".xlsx,.xls,.csv,.tsv";
const OMIT_KEYS_FROM_PERSISTENCE = new Set([
  "id",
  "index",
  "isNewRow",
  "isCarrierDisabled",
  "grossPPM",
  "grossPPMRelativeToLayerBelow",
]);

const HEADER_ALIASES = {
  carrier: ["carrier", "insurer", "market", "company"],
  limit: ["limit", "layerlimit", "layer limit"],
  attachment: ["attachment", "excess", "att"],
  grossPremium: [
    "marketgwp",
    "market gwp",
    "gwp",
    "premium",
    "grosspremium",
    "marketpremium",
  ],
};

const normaliseHeader = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const parseNumberish = (value) => {
  if (value == null) return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  const input = String(value).trim();
  if (!input) return null;
  const negativeParen = /^\((.*)\)$/.exec(input);
  const stripped = (negativeParen ? negativeParen[1] : input).replace(
    /[$,%\s,]/g,
    ""
  );
  const parsed = Number(stripped);
  if (!Number.isFinite(parsed)) return null;
  return negativeParen ? -parsed : parsed;
};

const getDisplayCarrier = (row) => {
  if (Array.isArray(row.shares) && row.shares.length > 0) {
    return row.shares
      .map((share) => {
        const carrier = share?.carrier?.trim?.() ?? "";
        return carrier || "[Unknown]";
      })
      .join(" / ");
  }
  return row.carrier ?? "";
};

const mapHeaderIndexes = (header) => {
  const normalisedHeaders = header.map(normaliseHeader);
  const mapping = {};
  Object.entries(HEADER_ALIASES).forEach(([field, aliases]) => {
    const index = normalisedHeaders.findIndex((columnName) =>
      aliases.some((alias) => normaliseHeader(alias) === columnName)
    );
    if (index >= 0) {
      mapping[field] = index;
    }
  });
  return mapping;
};

const rowsToImportedLayers = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  const firstRow = rows[0] ?? [];
  const headerMapping = mapHeaderIndexes(firstRow);
  const hasHeader = Object.keys(headerMapping).length >= 2;
  const defaultMapping = {
    carrier: 0,
    limit: 1,
    attachment: 2,
    grossPremium: 3,
  };
  const mapping = hasHeader ? headerMapping : defaultMapping;
  const dataRows = hasHeader ? rows.slice(1) : rows;

  return dataRows
    .map((row) => {
      const carrierRaw = row?.[mapping.carrier];
      const carrier =
        carrierRaw == null ? null : String(carrierRaw).trim() || null;
      const limit = parseNumberish(row?.[mapping.limit]);
      const attachment = parseNumberish(row?.[mapping.attachment]);
      const grossPremium = parseNumberish(row?.[mapping.grossPremium]);

      if (
        carrier == null &&
        limit == null &&
        attachment == null &&
        grossPremium == null
      ) {
        return null;
      }

      return {
        ...(carrier != null ? { carrier } : {}),
        ...(limit != null ? { limit } : {}),
        ...(attachment != null ? { attachment } : {}),
        ...(grossPremium != null ? { grossPremium } : {}),
      };
    })
    .filter(Boolean);
};

const rowsToStateLayers = (rows) =>
  rows
    .map((row, index) => {
      const layer = Object.entries(row).reduce((result, [key, value]) => {
        if (!OMIT_KEYS_FROM_PERSISTENCE.has(key)) {
          result[key] = value;
        }
        return result;
      }, {});

      const carrier =
        layer.carrier == null ? "" : String(layer.carrier).trim() || "";
      const limit = parseNumberish(layer.limit);
      const attachment = parseNumberish(layer.attachment);
      const grossPremium = parseNumberish(layer.grossPremium);

      if (carrier) layer.carrier = carrier;
      else delete layer.carrier;
      if (limit != null) layer.limit = limit;
      else delete layer.limit;
      if (attachment != null) layer.attachment = attachment;
      else delete layer.attachment;
      if (grossPremium != null) layer.grossPremium = grossPremium;
      else delete layer.grossPremium;

      const hasValue =
        layer.carrier != null ||
        layer.limit != null ||
        layer.attachment != null ||
        layer.grossPremium != null ||
        !!layer.shares;
      if (!hasValue) {
        return null;
      }

      const isLastDisplayRow = index === rows.length - 1;
      if (
        isLastDisplayRow &&
        layer.carrier == null &&
        layer.limit == null &&
        layer.attachment == null &&
        layer.grossPremium == null &&
        layer.shares == null
      ) {
        return null;
      }
      return layer;
    })
    .filter(Boolean);

export const ExpiringProgramComponent = (props) => {
  const {
    layers,
    updateAttachment,
    updateCarrier,
    updateLimit,
    updateMarketGrossWrittenPremium,
    deleteLayer,
    replaceLayers,
  } = props;
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);
  const pasteInProgressRef = useRef(false);
  const [activeCell, setActiveCell] = useState({ rowIndex: null, field: null });

  const rows = useMemo(
    () =>
      layers.map((layer, index) => ({
        ...layer,
        id: index,
        carrier: getDisplayCarrier(layer),
        isCarrierDisabled: !!layer.shares,
        isNewRow: index === layers.length - 1,
      })),
    [layers]
  );

  const updateField = useCallback(
    (index, field, value) => {
      switch (field) {
        case "carrier":
          updateCarrier(index, value);
          break;
        case "limit":
          updateLimit(index, value);
          break;
        case "attachment":
          updateAttachment(index, value);
          break;
        case "grossPremium":
          updateMarketGrossWrittenPremium(index, value);
          break;
        default:
          break;
      }
    },
    [
      updateAttachment,
      updateCarrier,
      updateLimit,
      updateMarketGrossWrittenPremium,
    ]
  );

  const columns = useMemo(
    () => [
      {
        field: "actions",
        type: "actions",
        width: 70,
        getActions: (params) =>
          params?.row?.isNewRow
            ? []
            : [
                <GridActionsCellItem
                  icon={<DeleteIcon />}
                  label={"Delete"}
                  onClick={() => deleteLayer(Number(params.id))}
                />,
              ],
      },
      {
        field: "carrier",
        headerName: translateLabel(labels.EXPIRING_PROGRAM_CARRIER, {
          default: "Carrier",
        }),
        description: "The name of the insurer.",
        type: "string",
        editable: true,
        minWidth: 220,
        flex: 1.2,
        sortable: false,
        cellClassName: (params) =>
          params?.row?.isCarrierDisabled ? "carrier-disabled" : "",
      },
      makePrettyNumberColDef({
        field: "limit",
        headerName: translateLabel(labels.EXPIRING_PROGRAM_LIMIT, {
          default: "Limit",
        }),
        description: "The layer limit.",
        width: 170,
        editable: true,
        dp: 0,
      }),
      makePrettyNumberColDef({
        field: "attachment",
        headerName: translateLabel(labels.EXPIRING_PROGRAM_ATTACHMENT, {
          default: "Attachment",
        }),
        description: "The layer attachment.",
        width: 170,
        editable: true,
        dp: 0,
      }),
      makePrettyNumberColDef({
        field: "grossPremium",
        headerName: translateLabel(labels.EXPIRING_PROGRAM_MARKET_GWP, {
          default: "Market GWP",
        }),
        description: "The 100% bound premium.",
        width: 170,
        editable: true,
        dp: 0,
      }),
      makePrettyNumberColDef({
        field: "grossPPM",
        headerName: translateLabel(labels.EXPIRING_PROGRAM_MARKET_RPM, {
          default: "Market RPM",
        }),
        description: "100% bound premium / limit.",
        width: 130,
        editable: false,
        dp: 0,
        cellClassName: "not-editable",
      }),
      makePrettyNumberColDef({
        field: "grossPPMRelativeToLayerBelow",
        headerName: translateLabel(labels.EXPIRING_PROGRAM_RATE_RELATIVITY, {
          default: "Rate Relativity",
        }),
        description: "Market RPM relative to the layer below (%).",
        width: 150,
        editable: false,
        dp: 2,
        multiplier: 100,
        suffix: "%",
        cellClassName: "not-editable",
      }),
    ],
    [deleteLayer, translateLabel]
  );

  const computeGrossPPM = useCallback((row) => {
    const limit = row?.limit;
    const grossPremium = row?.grossPremium;
    if (limit != null && grossPremium != null && limit !== 0) {
      return (grossPremium / limit) * 1e6;
    }
    return row?.grossPPM ?? null;
  }, []);

  const handleProcessRowUpdate = useCallback(
    (newRow, oldRow) => {
      if (pasteInProgressRef.current) {
        return newRow;
      }
      EDITABLE_FIELDS.forEach((field) => {
        if (newRow?.[field] !== oldRow?.[field]) {
          if (field === "carrier" && newRow?.isCarrierDisabled) {
            return;
          }
          updateField(Number(newRow.id), field, newRow?.[field] ?? null);
        }
      });
      const grossPPM = computeGrossPPM(newRow);
      const rowBelow = rows[Number(newRow.id) - 1];
      const grossPPMBelow = computeGrossPPM(rowBelow);
      return {
        ...newRow,
        grossPPM,
        grossPPMRelativeToLayerBelow:
          grossPPM != null && grossPPMBelow != null
            ? grossPPM / grossPPMBelow
            : null,
      };
    },
    [computeGrossPPM, rows, updateField]
  );

  const handlePaste = useCallback(
    (event) => {
      if (activeCell?.field == null || activeCell?.rowIndex == null) return;

      const text = event.clipboardData?.getData("text") ?? "";
      if (!/[\t\r\n]/.test(text)) return;

      const startColumn = EDITABLE_FIELDS.indexOf(activeCell.field);
      if (startColumn < 0) return;

      event.preventDefault();
      pasteInProgressRef.current = true;
      const matrix = text
        .trimEnd()
        .split(/\r?\n/)
        .map((line) => line.split("\t"));
      const nextRows = rows.map((row) => ({ ...row }));

      matrix.forEach((line, rowOffset) => {
        const rowIndex = activeCell.rowIndex + rowOffset;
        while (rowIndex >= nextRows.length) {
          nextRows.push({
            id: nextRows.length,
          });
        }
        line.forEach((cellValue, colOffset) => {
          const field = EDITABLE_FIELDS[startColumn + colOffset];
          if (!field) return;
          if (field === "carrier" && nextRows[rowIndex]?.isCarrierDisabled) {
            return;
          }
          nextRows[rowIndex][field] =
            field === "carrier"
              ? String(cellValue ?? "").trim()
              : parseNumberish(cellValue);
        });
      });

      replaceLayers(rowsToStateLayers(nextRows));
      setTimeout(() => {
        pasteInProgressRef.current = false;
      }, 0);
    },
    [activeCell.field, activeCell.rowIndex, replaceLayers, rows]
  );

  const handleImportChange = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;
      try {
        const workbookRows = await readFirstSheet(file);
        const importedLayers = rowsToImportedLayers(workbookRows);
        replaceLayers(importedLayers);
        enqueueSnackbar(`Imported ${importedLayers.length} layer(s)`, {
          variant: "success",
        });
      } catch {
        enqueueSnackbar("Failed to parse import file", {
          variant: "error",
        });
      } finally {
        event.target.value = "";
      }
    },
    [enqueueSnackbar, replaceLayers]
  );

  return (
    <Component
      title={translateLabel(labels.EXPIRING_PROGRAM_TITLE, {
        default: "Expiring Program",
      })}
      subtitle={translateLabel(labels.EXPIRING_PROGRAM_SUBTITLE, {
        default: "The expiring program layers.",
      })}
      options={
        <Stack direction={"row"} gap={1}>
          <Button
            size={"small"}
            onClick={() => {
              fileInputRef.current?.click();
            }}
          >
            {"Import from Excel"}
          </Button>
        </Stack>
      }
      lg_width={12}
    >
      <input
        ref={fileInputRef}
        type={"file"}
        accept={IMPORT_ACCEPT}
        style={{ display: "none" }}
        onChange={handleImportChange}
      />
      <Box
        onPaste={handlePaste}
        sx={{
          width: "100%",
          "& .not-editable": {
            backgroundColor: "#EFEFEF",
          },
          "& .MuiDataGrid-cell.not-editable:focus, & .MuiDataGrid-cell.not-editable:focus-within": {
            outline: "none",
          },
          "& .carrier-disabled": {
            backgroundColor: "#EFEFEF",
            color: "text.secondary",
          },
          "& .MuiDataGrid-cell.carrier-disabled:focus, & .MuiDataGrid-cell.carrier-disabled:focus-within": {
            outline: "none",
          },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          autoHeight
          hideFooter
          disableColumnMenu
          disableRowSelectionOnClick
          processRowUpdate={handleProcessRowUpdate}
          onCellClick={(params) => {
            setActiveCell({
              rowIndex: Number(params.id),
              field: params.field,
            });
          }}
          onCellEditStart={(params, event) => {
            if (params?.reason === "pasteKeyDown") {
              event.defaultMuiPrevented = true;
              return;
            }
            if (
              params?.reason === "deleteKeyDown" &&
              EDITABLE_FIELDS.includes(params.field) &&
              !(params.field === "carrier" && params?.row?.isCarrierDisabled)
            ) {
              updateField(Number(params.id), params.field, null);
              event.defaultMuiPrevented = true;
            }
          }}
          isCellEditable={(params) =>
            EDITABLE_FIELDS.includes(params.field) &&
            !(params.field === "carrier" && params?.row?.isCarrierDisabled)
          }
        />
      </Box>
    </Component>
  );
};

export const mapDispatchToProps = {
  updateLimit: expiringProgramActions.updateLimit,
  updateAttachment: expiringProgramActions.updateAttachment,
  updateMarketGrossWrittenPremium:
    expiringProgramActions.updateMarketGrossWrittenPremium,
  updateCarrier: expiringProgramActions.updateCarrier,
  deleteLayer: expiringProgramActions.deleteLayer,
  replaceLayers: expiringProgramActions.replaceLayers,
};

export const mapStateToProps = (state) => {
  return {
    layers: expiringProgramSelectors.getExpiringProgramLayersToDisplay(state),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ExpiringProgramComponent);
